import React, { useMemo, useState } from "react";
import { useAlert } from "react-alert";
import useAccountMetadata from "../hooks/useAccountMetadata";
import useFeatureFlags from "../hooks/useFeatureFlag";
import useCurrentEnvironment from "../hooks/use-environment";
import { useDispatch } from "react-redux";
import FeatureFlag, { getFeature } from "../FeatureFlag";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  FormControlLabel,
  Modal,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { makeRequest, userCanToggleRestrictedFeatures } from "../util";
import { RefreshIcon } from "../components/icons/themes/windows10";
import PropTypes from "prop-types";
import { fetchAccountMetadata } from "../actions/environments";
import clsx from "clsx";
import CloseIcon from "@material-ui/icons/Close";

export type FeatureToggleProps = {
  featureName: FeatureFlag | "objectSources";
  featureLabel: string;
  featureLevel: string;
  description: JSX.Element | string;
  disableIfEnabled?: boolean;
  restricted?: boolean;
};

function FeatureToggle({
  featureName,
  featureLabel,
  featureLevel,
  description,
  disableIfEnabled = false,
  restricted = false,
}: FeatureToggleProps): JSX.Element | null {
  const alert = useAlert();
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const currentEnvironment = useCurrentEnvironment();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // Prevent rendering default toggle values before account metadata loads
  if (!accountMetadata.loaded) {
    return null;
  }

  if (!currentEnvironment.loaded) {
    return null;
  }

  const featureState = useMemo(() => {
    if (featureLevel === "account") {
      return featureName === "objectSources"
        ? accountMetadata.hasObjectSources
        : flag(featureName);
    }

    if (featureLevel === "project") {
      return featureName === "objectSources"
        ? currentEnvironment.project.hasObjectSources
        : getFeature(currentEnvironment.project, featureName);
    }

    return false;
  }, [flag, featureName, accountMetadata, currentEnvironment]);

  function handleToggleFeature() {
    makeRequest("toggle_feature", {
      featureName,
      featureLevel,
      desiredState: !featureState,
    })
      .then(() => {
        dispatch(fetchAccountMetadata(alert));
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        window.Sentry.captureException(error);
        alert.error("Something went wrong.");
        setLoading(false);
      });
  }

  return (
    <>
      <span>
        <FormControlLabel
          style={{ width: "fit-content" }}
          control={
            <Switch
              disabled={
                isLoading ||
                (disableIfEnabled && featureState) ||
                (restricted && !userCanToggleRestrictedFeatures())
              }
              checked={featureState}
              onChange={() => setModalOpen(true)}
            />
          }
          label={<Typography variant="h5">{featureLabel}</Typography>}
        />
        {isLoading && <RefreshIcon className="animate-spin mr-2" />}
      </span>
      <ConfirmFeatureToggleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={() => {
          setLoading(true);
          // Add a short delay so that the loading icon is clearly visible
          setTimeout(() => handleToggleFeature(), 500);
        }}
        featureLabel={featureLabel}
        desiredState={!featureState}
      />
      <Typography variant="body1" component={"div"}>
        {description}
      </Typography>
      <br />
    </>
  );
}

FeatureToggle.propTypes = {
  featureName: PropTypes.string,
  featureLabel: PropTypes.string,
  disableIfEnabled: PropTypes.bool,
  restricted: PropTypes.bool,
  description: PropTypes.node,
};

export default FeatureToggle;

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
    overflowX: "visible",
    wordBreak: "break-all",
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

const ConfirmFeatureToggleModal = function ({
  open,
  onClose,
  onConfirm,
  featureLabel,
  desiredState,
}) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Confirm Feature Toggle"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            <span className={clsx(classes.bold)}>
              Are you sure you want to turn&nbsp;
              {featureLabel}&nbsp;
              {desiredState ? "ON" : "OFF"}?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.deactivateButton)}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
          >
            CONFIRM
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

ConfirmFeatureToggleModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  featureLabel: PropTypes.string,
  featureLevel: PropTypes.string,
  desiredState: PropTypes.bool,
};
